import { cn } from "@ndd/ui";

// RD Home's look under the always theme (NO-796; the approved spec is the
// NO-796 Linear comment "Approved design spec (Justin, 2026-10-22)", with the
// shared decisions on NO-791). Every class here is either a brand: utility on
// a plain element, or, on an @ndd/ui component that takes a className, a
// utility that reads a value only [data-theme="always"] defines with the
// classic value as its fallback (apps/always-ai/AGENTS.md). With the theme off
// the agenda renders exactly as before. The theme-only values sit in the
// "RD Home" block of client/src/styles.css. A leaf module, so `tsx --test`
// reads it.

/** A page title (an h1): display type, 32px under the theme. */
export const PAGE_TITLE = "brand:text-display brand:leading-[1.15]";

/** A schedule card: white with a hairline; under the theme, no drop shadow. */
export const AGENDA_CARD =
  "mt-3 overflow-hidden rounded-xl border border-border bg-card shadow-sm brand:shadow-none";

/** A card that only holds skeleton rows: the agenda card's frame. */
export const AGENDA_SKELETON_CARD =
  "rounded-xl border border-border bg-card shadow-sm brand:shadow-none";

const AGENDA_ROW =
  "group flex items-center gap-3.5 border-b border-sand-100 border-l-[3px] px-5 py-3 transition-colors last:border-b-0 hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring brand:border-b-border brand:border-l-0";

// The current row floats inside its card with a 1.5px olive outline.
const CURRENT_ROW =
  "border-l-info brand:mx-2 brand:my-1.5 brand:rounded-[14px] brand:border-b-transparent brand:px-3 brand:shadow-[inset_0_0_0_1.5px_var(--primary)]";

/** An agenda row (Upcoming or Past). Classic, the current row has a blue left edge. */
export function agendaRowClass(current: boolean): string {
  return cn(AGENDA_ROW, current ? CURRENT_ROW : "border-l-transparent");
}

/** A row's time: mono 13px plum-2 under the theme. */
export const AGENDA_TIME =
  "brand:font-mono brand:text-meta brand:font-normal brand:text-muted-foreground";

/**
 * A short label above what it names ("Happening now", "Up next"): mono caps
 * under the theme, read from the same values as `Eyebrow`, whose classic look
 * differs from this label's.
 */
export const AGENDA_EYEBROW =
  "brand:font-(family-name:--eyebrow-font) brand:font-(weight:--eyebrow-weight) brand:text-(length:--eyebrow-size) brand:text-(color:--eyebrow-ink)";

/**
 * The utility buttons' radius (Button variant utility): classic the pill,
 * 12px under the theme.
 */
export const AGENDA_UTILITY_RADIUS =
  "rounded-(--agenda-utility-radius,calc(infinity*1px))";

/**
 * The hero card's primary action (Button variant press). Classic it is the
 * bark pill; under the theme the press variant's olive, so the caller's fill
 * reads theme-only values instead of naming bark outright.
 */
export const AGENDA_PRIMARY_FILL =
  "bg-(--agenda-primary-fill,var(--color-bark)) text-(color:--agenda-primary-ink,var(--color-white)) hover:bg-(--agenda-primary-fill-hover,var(--color-bark-hover))";

/** The Upcoming/Past tab labels (TabsList variant underline). */
export const AGENDA_TAB_INK = "text-(color:--agenda-tab-ink,var(--color-muted-strong))";

/** "View full schedule in Healthie": a quiet olive link with a 1px underline. */
export const QUIET_LINK =
  "brand:font-sans brand:text-body-sm brand:font-bold brand:text-primary brand:underline brand:decoration-1 brand:underline-offset-3 brand:hover:text-primary-hover";
